import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { agentCategoryService } from "../../../../services/agentCategoryService";
import { agentService } from "../../../../services/agentService";
import { industryService } from "../../../../services/industryService";
import { aiPodService, type AiPod, type AiPodChannel, type CreateAiPodDto } from "../../../../services/aiPodService";
import { Badge } from '../../../../components/ui/Badge';
import { Button } from '../../../../components/ui/Button';
import { Checkbox, SelectField, TextArea, TextField } from '../../../../components/ui/Field';
import { Overlay } from '../../../../components/ui/Overlay';

/* ===================== TYPES ===================== */

type AgentTypeOption = {
  id: string;
  code: string;
  name: string;
  shortDescription?: string | null;
  isActive: boolean;
};

type Props = {
  isOpen: boolean;
  isEditing: boolean;
  aiPodDetails?: AiPod;
  onClose: () => void;
  refreshList: () => void;
};

type TitledItem = { title: string; body: string };

const CHANNEL_OPTIONS: Array<{ value: AiPodChannel; label: string; hint: string }> = [
  { value: "chat", label: "Chat", hint: "A chatbot with the pod's agents" },
  { value: "workflow", label: "Workflow", hint: "A workflow from the Configure canvas (voice, WhatsApp, webhook…)" },
  { value: "both", label: "Workflow + chat", hint: "One workflow and one chatbot" },
];

/** Small editor for a list of { title, body } rows. */
const TitledListEditor: React.FC<{ label: string; items: TitledItem[]; onChange: (items: TitledItem[]) => void }> = ({
  label,
  items,
  onChange,
}) => (
  <div>
    <div className="mb-1 flex items-center justify-between">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <Button tone="outline-gray" type="button" className="rounded border px-2 py-0.5 text-xs" onClick={() => onChange([...items, { title: "", body: "" }])}>
        + Add
      </Button>
    </div>
    {items.length === 0 ? (
      <p className="text-xs text-gray-400">None — section hidden.</p>
    ) : (
      <div className="space-y-2">
        {items.map((item, idx) => (
          <div key={idx} className="grid grid-cols-[1fr_2fr_auto] gap-2">
            <TextField
              className="w-full border px-2 py-1.5 rounded text-sm"
              placeholder="Title"
              value={item.title}
              onChange={(e) => onChange(items.map((x, i) => (i === idx ? { ...x, title: e.target.value } : x)))}
            />
            <TextField
              className="w-full border px-2 py-1.5 rounded text-sm"
              placeholder="Description"
              value={item.body}
              onChange={(e) => onChange(items.map((x, i) => (i === idx ? { ...x, body: e.target.value } : x)))}
            />
            <Button tone="danger-soft" type="button" className="rounded border px-2 text-xs" onClick={() => onChange(items.filter((_, i) => i !== idx))}>
              ✕
            </Button>
          </div>
        ))}
      </div>
    )}
  </div>
);

/* ===================== COMPONENT ===================== */

const AiPodModal: React.FC<Props> = ({ isOpen, isEditing, aiPodDetails, onClose, refreshList }) => {
  const [name, setName] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [description, setDescription] = useState("");
  const [industryId, setIndustryId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [version, setVersion] = useState<number | undefined>(undefined);

  const [industryOptions, setIndustryOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [categoryOptions, setCategoryOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [availableAgentTypes, setAvailableAgentTypes] = useState<AgentTypeOption[]>([]);
  const [pipeline, setPipeline] = useState<AgentTypeOption[]>([]);
  const [agentTypeSearch, setAgentTypeSearch] = useState("");

  // What a subscription provisions. Pricing lives in Manage Plans, not here.
  const [channel, setChannel] = useState<AiPodChannel>("workflow");
  // Marketplace content shown on the tenant pod pages (stored in pod metadata).
  const [modesText, setModesText] = useState("");
  const [badge, setBadge] = useState("");
  const [channelsText, setChannelsText] = useState("");
  const [highlights, setHighlights] = useState<TitledItem[]>([]);
  const [howItWorks, setHowItWorks] = useState<TitledItem[]>([]);
  const [sidebarText, setSidebarText] = useState("");
  const [pairWith, setPairWith] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* ---------- Load reference data ---------- */
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    (async () => {
      try {
        const [industriesRes, categoriesRes, agentTypesRes] = await Promise.all([
          industryService.list({ limit: 100, isActive: true }),
          agentCategoryService.list({ limit: 100, isActive: true }),
          agentService.listAgentTypes({ limit: 100, isActive: true }),
        ]);

        const industriesRaw: any =
          (industriesRes as any)?.industries || (industriesRes as any)?.data?.industries || [];
        const categoriesRaw: any =
          (categoriesRes as any)?.agentCategories || (categoriesRes as any)?.data?.agentCategories || [];
        const agentTypesRaw: any =
          (agentTypesRes as any)?.agentTypes || (agentTypesRes as any)?.data?.agentTypes || [];

        if (!cancelled) {
          setIndustryOptions(
            Array.isArray(industriesRaw) ? industriesRaw.map((i: any) => ({ id: String(i.id), name: String(i.name) })) : []
          );
          setCategoryOptions(
            Array.isArray(categoriesRaw) ? categoriesRaw.map((c: any) => ({ id: String(c.id), name: String(c.name) })) : []
          );
          setAvailableAgentTypes(
            Array.isArray(agentTypesRaw)
              ? agentTypesRaw.map((a: any) => ({
                  id: a.id,
                  code: a.code,
                  name: a.name,
                  shortDescription: a.shortDescription,
                  isActive: a.isActive,
                }))
              : []
          );
        }
      } catch {
        if (!cancelled) toast.error("Failed to load industry/category/agent type options");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  /* ---------- Edit mode: hydrate from existing pod ---------- */
  useEffect(() => {
    if (!aiPodDetails) return;

    setName(aiPodDetails.name || "");
    setShortDescription(aiPodDetails.shortDescription || "");
    setDescription(aiPodDetails.description || "");
    setIndustryId(aiPodDetails.industryId || aiPodDetails.industry?.id || "");
    setCategoryId(aiPodDetails.categoryId || aiPodDetails.category?.id || "");
    setIsActive(!!aiPodDetails.isActive);
    setVersion(aiPodDetails.version);

    const ordered = [...(aiPodDetails.agentTypes || [])].sort((a, b) => a.displayOrder - b.displayOrder);
    setPipeline(
      ordered.map((ref) => ({
        id: ref.agentType.id,
        code: ref.agentType.code,
        name: ref.agentType.name,
        shortDescription: ref.agentType.shortDescription,
        isActive: ref.agentType.isActive,
      }))
    );

    const meta = (aiPodDetails.metadata ?? {}) as Record<string, any>;
    setChannel((aiPodDetails.channel as AiPodChannel) ?? "workflow");
    setModesText(Array.isArray(meta.modes) ? meta.modes.join(", ") : "");
    setBadge(typeof meta.badge === "string" ? meta.badge : "");
    setChannelsText(Array.isArray(meta.channels) ? meta.channels.join(", ") : "");
    setHighlights(Array.isArray(meta.highlights) ? meta.highlights : []);
    setHowItWorks(Array.isArray(meta.howItWorks) ? meta.howItWorks : []);
    setSidebarText(Array.isArray(meta.sidebarFeatures) ? meta.sidebarFeatures.join("\n") : "");
    setPairWith(typeof meta.pairWith === "string" ? meta.pairWith : "");
  }, [aiPodDetails]);

  /* ---------- Create mode: reset ---------- */
  useEffect(() => {
    if (!isOpen || aiPodDetails) return;
    setName("");
    setShortDescription("");
    setDescription("");
    setIndustryId("");
    setCategoryId("");
    setIsActive(true);
    setVersion(undefined);
    setPipeline([]);
    setChannel("workflow");
    setModesText("");
    setBadge("");
    setChannelsText("");
    setHighlights([]);
    setHowItWorks([]);
    setSidebarText("");
    setPairWith("");
    setError(null);
  }, [isOpen, aiPodDetails]);

  const pipelineIds = useMemo(() => new Set(pipeline.map((a) => a.id)), [pipeline]);

  const filteredAvailable = useMemo(() => {
    const q = agentTypeSearch.trim().toLowerCase();
    return availableAgentTypes.filter((a) => {
      if (pipelineIds.has(a.id)) return false;
      if (!q) return true;
      return a.name.toLowerCase().includes(q) || a.code.toLowerCase().includes(q);
    });
  }, [availableAgentTypes, pipelineIds, agentTypeSearch]);

  const addToPipeline = (agentType: AgentTypeOption) => {
    setPipeline((prev) => [...prev, agentType]);
  };

  const removeFromPipeline = (id: string) => {
    setPipeline((prev) => prev.filter((a) => a.id !== id));
  };

  const moveInPipeline = (index: number, direction: -1 | 1) => {
    setPipeline((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  /* ---------- Save ---------- */
  const handleSave = async () => {
    if (!name.trim()) {
      setError("Pod name is required");
      return;
    }
    if (!pipeline.length) {
      setError("Add at least one agent type to the pipeline");
      return;
    }

    const splitList = (text: string, sep: RegExp) =>
      text.split(sep).map((x) => x.trim()).filter(Boolean);
    const metadata: Record<string, unknown> = {
      modes: splitList(modesText, /,/),
      badge: badge.trim() || null,
      channels: splitList(channelsText, /,/),
      highlights: highlights.filter((h) => h.title.trim()).map((h) => ({ title: h.title.trim(), body: h.body.trim() })),
      howItWorks: howItWorks.filter((h) => h.title.trim()).map((h) => ({ title: h.title.trim(), body: h.body.trim() })),
      sidebarFeatures: splitList(sidebarText, /\n/),
      pairWith: pairWith.trim() || null,
    };

    const payload: CreateAiPodDto = {
      name: name.trim(),
      shortDescription: shortDescription.trim() || undefined,
      description: description.trim() || undefined,
      industryId: industryId || undefined,
      categoryId: categoryId || undefined,
      isActive,
      metadata,
      channel,
      agentTypeIds: pipeline.map((a) => a.id),
    };

    try {
      setLoading(true);
      if (isEditing && aiPodDetails) {
        await aiPodService.updateAiPod(aiPodDetails.id, { ...payload, version });
        toast.success("AI Pod updated");
      } else {
        await aiPodService.createAiPod(payload);
        toast.success("AI Pod created");
      }
      await refreshList();
      onClose();
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || "Failed to save AI Pod");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  /* ===================== UI ===================== */

  return (
    <Overlay className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => {
          if (!loading && window.confirm("Discard changes?")) onClose();
        }}
      />

      <div className="relative w-[75vw] max-h-[85vh] overflow-y-auto bg-white rounded-xl shadow-xl p-6 space-y-6">
        <div>
          <h2 className="text-xl font-semibold">{isEditing ? "Edit AI Pod" : "Create AI Pod"}</h2>
          <p className="text-sm text-gray-500">
            Bundle existing agent types into an ordered pipeline tenants can purchase as one unit.
          </p>
        </div>

        {error && <div className="text-sm text-red-700 bg-red-50 border p-2 rounded">{error}</div>}

        {/* Basic Info */}
        <section className="grid grid-cols-1 gap-4">
          <Field label="Pod Name">
            <TextField className="w-full border px-3 py-2 rounded" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Short Description">
            <TextField
              className="w-full border px-3 py-2 rounded"
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value)}
            />
          </Field>
        </section>

        <section className="grid grid-cols-2 gap-4">
          <Field label="Industry">
            <SelectField
              className="w-full border rounded px-3 py-2"
              value={industryId}
              onChange={(e) => setIndustryId(e.target.value)}
            >
              <option value="">-- Select industry --</option>
              {industryOptions.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </SelectField>
          </Field>
          <Field label="Category">
            <SelectField
              className="w-full border rounded px-3 py-2"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <option value="">-- Select category --</option>
              {categoryOptions.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </SelectField>
          </Field>
        </section>

        <Field label="Description">
          <TextArea
            className="w-full border px-3 py-2 rounded"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>

        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          Active
        </label>

        {/* Pipeline composer */}
        <section className="space-y-3 border-t border-gray-200 pt-4">
          <h3 className="font-semibold text-base">Agent Type Pipeline</h3>
          <p className="text-xs text-gray-500">
            Order matters — this is the sequence tenants' agent instances will run in once the pod is activated.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Available agent types */}
            <div className="border rounded-lg p-3 space-y-2">
              <TextField
                className="w-full border px-3 py-2 rounded text-sm"
                placeholder="Search agent types…"
                value={agentTypeSearch}
                onChange={(e) => setAgentTypeSearch(e.target.value)}
              />
              <div className="max-h-72 overflow-y-auto space-y-2">
                {filteredAvailable.length === 0 ? (
                  <p className="text-xs text-gray-500 py-4 text-center">No available agent types</p>
                ) : (
                  filteredAvailable.map((a) => (
                    <div
                      key={a.id}
                      className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50/60 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-800">{a.name}</p>
                        <p className="truncate text-xs text-gray-500">{a.code}</p>
                      </div>
                      <Button tone="outline-gray"
                        type="button"
                        className="shrink-0 rounded-lg border px-2 py-1 text-xs"
                        onClick={() => addToPipeline(a)}
                      >
                        Add →
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Ordered pipeline */}
            <div className="border rounded-lg p-3 space-y-2">
              <p className="text-xs font-medium text-gray-600">Pipeline ({pipeline.length})</p>
              <div className="max-h-72 overflow-y-auto space-y-2">
                {pipeline.length === 0 ? (
                  <p className="text-xs text-gray-500 py-4 text-center">Add agent types from the left</p>
                ) : (
                  pipeline.map((a, idx) => (
                    <div
                      key={a.id}
                      className="flex items-center gap-2 rounded-lg border border-agentic-primary-blue/30 bg-agentic-primary-blue/5 px-3 py-2"
                    >
                      <Badge className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-agentic-primary-blue text-xs font-semibold text-white">
                        {idx + 1}
                      </Badge>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-800">{a.name}</p>
                        <p className="truncate text-xs text-gray-500">{a.code}</p>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <Button tone="outline-gray"
                          type="button"
                          title="Move up"
                          className="rounded border px-1.5 py-0.5 text-xs disabled:opacity-30"
                          disabled={idx === 0}
                          onClick={() => moveInPipeline(idx, -1)}
                        >
                          ↑
                        </Button>
                        <Button tone="outline-gray"
                          type="button"
                          title="Move down"
                          className="rounded border px-1.5 py-0.5 text-xs disabled:opacity-30"
                          disabled={idx === pipeline.length - 1}
                          onClick={() => moveInPipeline(idx, 1)}
                        >
                          ↓
                        </Button>
                        <Button tone="danger-soft"
                          type="button"
                          title="Remove"
                          className="rounded border px-1.5 py-0.5 text-xs"
                          onClick={() => removeFromPipeline(a.id)}
                        >
                          ✕
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </section>

        {/* What subscribers get */}
        <section className="space-y-3 border-t border-gray-200 pt-4">
          <h3 className="font-semibold text-base">What subscribers get</h3>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {CHANNEL_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className={`flex cursor-pointer flex-col rounded-lg border px-3 py-2 text-sm ${
                  channel === opt.value ? "border-agentic-primary-blue bg-agentic-primary-blue/5" : "border-gray-200"
                }`}
              >
                <span className="flex items-center gap-2 font-medium text-gray-800">
                  <input type="radio" name="pod-channel" checked={channel === opt.value} onChange={() => setChannel(opt.value)} />
                  {opt.label}
                </span>
                <span className="mt-1 text-xs text-gray-500">{opt.hint}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-gray-500">
            Plans and prices are set under Manage Plans. Changing this affects new subscribers only.
          </p>
        </section>

        {/* Marketplace content */}
        <section className="space-y-3 border-t border-gray-200 pt-4">
          <h3 className="font-semibold text-base">Marketplace content</h3>
          <p className="text-xs text-gray-500">Shown to tenants on the pod card and detail page. Leave a section empty to hide it.</p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="How it works tags (comma separated: Talks, Chats, Automated)">
              <TextField className="w-full border px-3 py-2 rounded" value={modesText} onChange={(e) => setModesText(e.target.value)} />
            </Field>
            <Field label="Channel chips (comma separated, e.g. Phone, Website chat)">
              <TextField className="w-full border px-3 py-2 rounded" value={channelsText} onChange={(e) => setChannelsText(e.target.value)} />
            </Field>
            <Field label="Card badge (e.g. Most popular)">
              <TextField className="w-full border px-3 py-2 rounded" value={badge} onChange={(e) => setBadge(e.target.value)} />
            </Field>
            <Field label="Works well with (pod name)">
              <TextField className="w-full border px-3 py-2 rounded" value={pairWith} onChange={(e) => setPairWith(e.target.value)} />
            </Field>
          </div>
          <TitledListEditor label="Highlights" items={highlights} onChange={setHighlights} />
          <TitledListEditor label="How this pod works" items={howItWorks} onChange={setHowItWorks} />
          <Field label="Sidebar checklist (one item per line)">
            <TextArea className="w-full border px-3 py-2 rounded" rows={3} value={sidebarText} onChange={(e) => setSidebarText(e.target.value)} />
          </Field>
        </section>

        {/* Footer */}
        <div className="flex justify-end gap-3">
          <Button tone="custom" className="border px-4 py-2 rounded" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button tone="blue-static"
            className="px-4 py-2 rounded disabled:opacity-50"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? "Saving..." : isEditing ? "Save Changes" : "Create AI Pod"}
          </Button>
        </div>
      </div>
    </Overlay>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
    {children}
  </div>
);

export default AiPodModal;
